import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Eraser, Save } from 'lucide-react';

/**
 * UnitEditor — admin side panel for authoring unit footprints (Sprint 3 D3).
 *
 * Holds no state of its own; TwinPage owns the editable `units` array and passes
 * mutators. Selecting a unit makes it active so clicks on FloorPlanCanvas append
 * vertices to its polygon.
 */
const STATUSES = ['occupied', 'vacant', 'under-fitout'];

const fieldStyle = {
    width: '100%', padding: '6px 8px', fontSize: 13, marginTop: 2,
    background: 'var(--v3-input-bg, rgba(255,255,255,.04))',
    border: '1px solid var(--v3-glass-border, rgba(255,255,255,.12))',
    borderRadius: 4, color: 'inherit',
};
const labelStyle = { fontSize: 11, color: 'var(--v3-text-muted)', display: 'block', marginTop: 8 };

export default function UnitEditor({
    units = [],
    activeUnitId,
    setActiveUnitId,
    onAddUnit,
    onChangeUnit,
    onRemoveUnit,
    onClearPolygon,
    onSave,
    saving,
    saveError,
    dirty,
}) {
    const { t } = useTranslation();
    const active = units.find((u) => u.unit_id === activeUnitId) || null;

    return (
        <div className="v3-card v3-cc-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>{t('Units ({{count}})', { count: units.length })}</h3>
                <button className="v3-btn v3-btn-ghost" onClick={onAddUnit} type="button">
                    <Plus size={14} /> {t('Add')}
                </button>
            </div>

            {/* Unit list */}
            <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {units.length === 0 && (
                    <div className="v3-text-muted" style={{ fontSize: 12 }}>{t('No units yet. Click “Add”, then click on the floor plan to draw.')}</div>
                )}
                {units.map((u) => (
                    <button
                        key={u.unit_id}
                        type="button"
                        onClick={() => setActiveUnitId(u.unit_id)}
                        style={{
                            textAlign: 'left', padding: '6px 8px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
                            border: '1px solid var(--v3-glass-border, rgba(255,255,255,.12))',
                            background: u.unit_id === activeUnitId ? 'rgba(14,165,233,.18)' : 'transparent',
                            color: 'inherit',
                        }}
                    >
                        <strong>{u.unit_id}</strong> · {u.tenant_name || '—'}
                        {' '}<span className="v3-text-muted">({t('{{count}} pts', { count: u.polygon?.length || 0 })})</span>
                    </button>
                ))}
            </div>

            {/* Active unit form */}
            {active && (
                <div style={{ borderTop: '1px solid var(--v3-glass-border)', paddingTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{t('Editing {{id}}', { id: active.unit_id })}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button className="v3-btn v3-btn-ghost" type="button" title={t('Clear polygon')} onClick={() => onClearPolygon(active.unit_id)}>
                                <Eraser size={13} />
                            </button>
                            <button className="v3-btn v3-btn-ghost" type="button" title={t('Delete unit')} onClick={() => onRemoveUnit(active.unit_id)}>
                                <Trash2 size={13} />
                            </button>
                        </div>
                    </div>

                    <label style={labelStyle}>{t('Unit ID')}
                        <input style={fieldStyle} value={active.unit_id}
                            onChange={(e) => onChangeUnit(active.unit_id, { unit_id: e.target.value })} />
                    </label>
                    <label style={labelStyle}>{t('Tenant name')}
                        <input style={fieldStyle} value={active.tenant_name || ''}
                            onChange={(e) => onChangeUnit(active.unit_id, { tenant_name: e.target.value })} />
                    </label>
                    <label style={labelStyle}>{t('Tenant ID (optional)')}
                        <input style={fieldStyle} value={active.tenant_id || ''}
                            onChange={(e) => onChangeUnit(active.unit_id, { tenant_id: e.target.value })} />
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <label style={{ ...labelStyle, flex: 1 }}>{t('Floor')}
                            <input style={fieldStyle} type="number" value={active.floor ?? ''}
                                onChange={(e) => onChangeUnit(active.unit_id, { floor: e.target.value })} />
                        </label>
                        <label style={{ ...labelStyle, flex: 1 }}>{t('Size (sqft)')}
                            <input style={fieldStyle} type="number" value={active.size_sqft ?? ''}
                                onChange={(e) => onChangeUnit(active.unit_id, { size_sqft: e.target.value })} />
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <label style={{ ...labelStyle, flex: 1 }}>{t('Zone')}
                            <input style={fieldStyle} value={active.zone || ''}
                                onChange={(e) => onChangeUnit(active.unit_id, { zone: e.target.value })} />
                        </label>
                        <label style={{ ...labelStyle, flex: 1 }}>{t('Status')}
                            <select style={fieldStyle} value={active.status || 'vacant'}
                                onChange={(e) => onChangeUnit(active.unit_id, { status: e.target.value })}>
                                {STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="v3-text-muted" style={{ fontSize: 11, marginTop: 8 }}>
                        {t('Click the floor plan to add points · drag a point to move · right-click a point to remove.')}
                    </div>
                </div>
            )}

            {saveError && <div className="v3-text-danger" style={{ fontSize: 12 }}>{saveError}</div>}
            <button className="v3-btn v3-btn-primary" type="button" onClick={onSave} disabled={saving || !dirty}>
                <Save size={14} /> {saving ? t('Saving…') : t('Save all units')}
            </button>
        </div>
    );
}
